import { getAccessToken } from '@/lib/authToken';
import { fetchJson, TransientHtmlError, TransientParseError } from '@/lib/fetchJson';

export type CheckoutResultEventDetail = {
  paymentId: string | number;
  status: 'SUCCESS' | 'FAILED' | 'PENDING';
  invoiceUrl?: string | null;
  message?: string;
};

export function publishCheckoutResult(detail: CheckoutResultEventDetail) {
  window.dispatchEvent(new CustomEvent('checkout-result', { detail }));
}

function normalizePaymentStatus(value: any): CheckoutResultEventDetail['status'] {
  const raw = String(value || '').trim().toUpperCase();
  if (raw === 'SUCCESS' || raw === 'SUCCEEDED' || raw === 'PAID' || raw === 'COMPLETED') return 'SUCCESS';
  if (raw === 'FAILED' || raw === 'FAIL' || raw === 'ERROR' || raw === 'CANCELLED' || raw === 'CANCELED') return 'FAILED';
  return 'PENDING';
}

async function pollPaymentStatusUntilFinal(
  paymentId: string | number,
  headers?: Record<string, string>,
): Promise<CheckoutResultEventDetail> {
  const maxAttempts = 40;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const statusRes = await fetchJson(`/api/payments/status/${paymentId}`, {
        method: 'GET',
        headers,
      });
      const status = normalizePaymentStatus(statusRes?.status);

      if (status !== 'PENDING') {
        return {
          paymentId,
          status,
          invoiceUrl: null,
          message: statusRes?.message,
        };
      }
    } catch {
      // Ignore transient errors and continue polling.
    }

    await new Promise((resolve) => window.setTimeout(resolve, 2000));
  }

  return {
    paymentId,
    status: 'PENDING',
    invoiceUrl: null,
    message: 'Still confirming your payment...',
  };
}

export async function triggerCheckoutWait(paymentId: string | number) {
  publishCheckoutResult({
    paymentId,
    status: 'PENDING',
    invoiceUrl: null,
    message: 'Still confirming your payment...',
  });

  const token = getAccessToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const qs = new URLSearchParams({ timeout: '60', include_invoice: 'true', poll_interval: '1.5' });
  try {
    const res = await fetchJson(`/api/payments/transactions/${paymentId}/wait?${qs.toString()}`, {
      method: 'GET',
      headers,
    });
    let detail: CheckoutResultEventDetail = {
      paymentId,
      status: normalizePaymentStatus(res?.status),
      invoiceUrl: res?.invoice_public_url || res?.invoice_presigned_url || null,
      message: res?.message,
    };

    if (detail.status === 'PENDING' && res?.timed_out) {
      const polled = await pollPaymentStatusUntilFinal(paymentId, headers);
      detail = {
        ...polled,
        invoiceUrl: detail.invoiceUrl,
        message: polled.status === 'PENDING' ? 'Still confirming your payment...' : polled.message,
      };
    }

    publishCheckoutResult(detail);
    return detail;
  } catch (e: any) {
    // Treat HTML/parse issues as transient during overlay; do NOT surface raw message
    if (e instanceof TransientHtmlError || e instanceof TransientParseError) {
      const detail: CheckoutResultEventDetail = {
        paymentId,
        status: 'PENDING',
        invoiceUrl: null,
        message: 'Still confirming your payment...',
      };
      publishCheckoutResult(detail);
      return detail;
    }
    const detail: CheckoutResultEventDetail = {
      paymentId,
      status: 'FAILED',
      invoiceUrl: null,
      message: 'Payment verification failed',
    };
    publishCheckoutResult(detail);
    return detail;
  }
}
